import { useCallback } from 'react';
import { JobCountQuery } from '@doda/common/src/services/getJobCountAPI';
import {
  AreaResponse,
  IndustryLResponse,
  JobTypeLResponse,
} from '@doda/api-client/aspida/@types';
import { CompanyOfSearchModal } from '@doda/common/src/models/MasterCacheModel';
import { SelectedCurrentConditionType } from 'types/searchCondition';
import {
  createIndustryLargeData,
  createIndustrySmallData,
  IndustryData,
} from 'logics/createIndustryInitialList';
import {
  AreaData,
  createAreaData,
  createCityData,
  createLocationDisplayList,
  createPrefectureData,
  createWardData,
} from 'logics/createLocationInitialList';
import { OccupationDisplayListType } from 'types/occupation';
import {
  createOccupationDisplayList,
  createOccupationLargeData,
  createOccupationMediumData,
  createOccupationSmallData,
} from 'logics/createOccupationInitialList';
import {
  createRouteData,
  createStationData,
  RouteData,
} from 'logics/createRouteStationInitialList';

interface ArgumentType {
  selectedCurrentCondition: SelectedCurrentConditionType;
  setOccupationDisplayList: React.Dispatch<
    React.SetStateAction<OccupationDisplayListType[]>
  >;
  setLocationDisplayList: React.Dispatch<React.SetStateAction<AreaData[]>>;
  setRouteStationDisplayList: React.Dispatch<React.SetStateAction<RouteData[]>>;
  setIndustryDisplayList: React.Dispatch<React.SetStateAction<IndustryData[]>>;
  updateJobCount: (currentCondition?: JobCountQuery) => Promise<void>;
  masterData: {
    occupationMasterData: JobTypeLResponse[];
    areaMasterData: AreaResponse[];
    routeMasterData: CompanyOfSearchModal[];
    industryMasterData: IndustryLResponse[];
  };
}

/**
 * モーダル用の検索条件に関するhooks
 * @param {object} param モーダル用の検索条件設定処理の生成用の引数
 * @param param.selectedCurrentCondition 現在選択されている詳細条件
 * @param param.setOccupationDisplayList 職種の表示用の更新関数
 * @param param.setLocationDisplayList 勤務地の表示用の更新関数
 * @param param.setRouteStationDisplayList 路線・駅の表示用の更新関数
 * @param param.setIndustryDisplayList 業種の表示用の更新関数
 * @param param.updateJobCount  求人のファセット件数の更新関数
 * @param param.masterData マスタデータ
 * @returns モーダル用の検索条件設定処理を含むオブジェクト
 */
export const useSettingConditionOnModal = ({
  selectedCurrentCondition,
  setOccupationDisplayList,
  setLocationDisplayList,
  setRouteStationDisplayList,
  setIndustryDisplayList,
  updateJobCount,
  masterData,
}: ArgumentType) => {
  const {
    occupationMasterData,
    areaMasterData,
    routeMasterData,
    industryMasterData,
  } = masterData;

  // 職種モーダルでの検索条件設定処理
  const onClickOccupationModalSettingButton = useCallback(
    async (selectedOccupation: {
      lList: SelectedCurrentConditionType['searchJobTypeLId'];
      mList: SelectedCurrentConditionType['searchJobTypeMId'];
      sList: SelectedCurrentConditionType['searchJobTypeSId'];
    }) => {
      // 画面表示用のチェックボックスリスト再生成
      const newOccupationSList = createOccupationSmallData(
        occupationMasterData,
        selectedOccupation.sList
      );
      const newOccupationMList = createOccupationMediumData(
        occupationMasterData,
        selectedOccupation.mList,
        newOccupationSList
      );
      const newOccupationLList = createOccupationLargeData(
        occupationMasterData,
        selectedOccupation.lList,
        newOccupationMList
      );
      const newOccupationDisplayList: OccupationDisplayListType[] =
        createOccupationDisplayList({
          occupationLList: newOccupationLList,
          occupationMList: newOccupationMList,
          occupationSList: newOccupationSList,
        });

      setOccupationDisplayList(newOccupationDisplayList);

      updateJobCount({
        searchJobTypeLId: selectedOccupation.lList,
        searchJobTypeMId: selectedOccupation.mList,
        searchJobTypeSId: selectedOccupation.sList,
      });

      selectedCurrentCondition.searchJobTypeLId = selectedOccupation.lList;
      selectedCurrentCondition.searchJobTypeMId = selectedOccupation.mList;
      selectedCurrentCondition.searchJobTypeSId = selectedOccupation.sList;
    },
    [
      occupationMasterData,
      selectedCurrentCondition,
      setOccupationDisplayList,
      updateJobCount,
    ]
  );

  // 勤務地モーダルでの検索条件設定処理
  const onClickLocationModalSettingButton = useCallback(
    async (selectedLocation: {
      areaList: string[];
      prefectureList: string[];
      cityList: string[];
      wardList: string[];
    }) => {
      // 画面表示用のチェックボックスリスト再生成
      const newWardList = createWardData(
        areaMasterData,
        selectedLocation.wardList
      );
      const newCityList = createCityData(
        areaMasterData,
        selectedLocation.cityList,
        newWardList
      );
      const newPrefectureList = createPrefectureData(
        areaMasterData,
        selectedLocation.prefectureList,
        newCityList
      );
      const newAreaList = createAreaData(
        areaMasterData,
        selectedLocation.areaList,
        newPrefectureList
      );
      const newLocationDisplayList: AreaData[] = createLocationDisplayList({
        areaList: newAreaList,
        prefectureList: newPrefectureList,
        cityList: newCityList,
        wardList: newWardList,
      });

      setLocationDisplayList(newLocationDisplayList);

      updateJobCount({
        areaId: selectedLocation.areaList,
        prefectureId: selectedLocation.prefectureList,
        cityId: selectedLocation.cityList,
        wardId: selectedLocation.wardList,
      });

      selectedCurrentCondition.areaId = selectedLocation.areaList;
      selectedCurrentCondition.prefectureId = selectedLocation.prefectureList;
      selectedCurrentCondition.cityId = selectedLocation.cityList;
      selectedCurrentCondition.wardId = selectedLocation.wardList;
    },
    [
      areaMasterData,
      selectedCurrentCondition,
      setLocationDisplayList,
      updateJobCount,
    ]
  );

  // 路線駅モーダルでの検索条件設定処理
  const onClickRouteStationModalSettingButton = useCallback(
    async (selectedRouteStation: {
      routeList: string[];
      stationList: string[];
    }) => {
      // 画面表示用のチェックボックスリスト再生成
      const routeList = createRouteData(
        routeMasterData,
        selectedRouteStation.routeList
      );
      const stationList = createStationData(
        routeMasterData,
        selectedRouteStation.stationList
      );
      const initialRouteStationDisplayList = routeList.map((route) => ({
        ...route,
        stationList: stationList.filter(({ routeId }) => routeId === route.id),
      }));

      setRouteStationDisplayList(initialRouteStationDisplayList);

      updateJobCount({
        routeId: selectedRouteStation.routeList,
        stationId: selectedRouteStation.stationList,
      });

      selectedCurrentCondition.routeId = selectedRouteStation.routeList;
      selectedCurrentCondition.stationId = selectedRouteStation.stationList;
    },
    [
      routeMasterData,
      selectedCurrentCondition,
      setRouteStationDisplayList,
      updateJobCount,
    ]
  );

  // 業種モーダルでの検索条件設定処理
  const onClickIndustryModalSettingButton = useCallback(
    async (selectedIndustry: { lList: string[]; sList: string[] }) => {
      // 画面表示用のチェックボックスリスト再生成
      const newIndustrySList = createIndustrySmallData(
        industryMasterData,
        selectedIndustry.sList
      );
      const newIndustryLList = createIndustryLargeData(
        industryMasterData,
        selectedIndustry.lList,
        newIndustrySList
      );
      const newIndustryDisplayList = newIndustryLList.map((industryL) => ({
        ...industryL,
        industrySList: newIndustrySList.filter(
          ({ parentId }) => parentId === industryL.id
        ),
      }));

      setIndustryDisplayList(newIndustryDisplayList);

      updateJobCount({
        industryLId: selectedIndustry.lList,
        industrySId: selectedIndustry.sList,
      });

      selectedCurrentCondition.industryLId = selectedIndustry.lList;
      selectedCurrentCondition.industrySId = selectedIndustry.sList;
    },
    [
      industryMasterData,
      selectedCurrentCondition,
      setIndustryDisplayList,
      updateJobCount,
    ]
  );

  return {
    onClickOccupationModalSettingButton,
    onClickLocationModalSettingButton,
    onClickRouteStationModalSettingButton,
    onClickIndustryModalSettingButton,
  };
};
